import StatementOfAccount from '@/pdf/statement-of-account.pdf'
import consumerLoanRepository from '@/repositories/consumer-loan.repository'
import { authAxios } from '@/extra-functions/axios.extra'
import { formatAccounting, getCurrentDateEnUsFormat } from '@/lib/utils'

function computeDaysOverdue(dateDue, balance) {
  if (!dateDue || balance <= 0) return 0
  const due = new Date(dateDue)
  const today = new Date()
  const diff = Math.floor((today.getTime() - due.getTime()) / 86400000)
  return diff > 0 ? diff : 0
}

async function getCompanyBranding() {
  const branding = {
    companyName: '',
    logoDataUrl: '',
    logoFormat: 'PNG',
    approvedByName: '',
    approvedByPosition: '',
  }
  try {
    const { data } = await authAxios().get('/company-info')
    branding.companyName = data?.company_name || ''
    branding.approvedByName = data?.approved_by_name || ''
    branding.approvedByPosition = data?.approved_by_position || ''
    if (data?.company_logo_url) {
      const res = await fetch(data.company_logo_url)
      const blob = await res.blob()
      branding.logoFormat = blob.type === 'image/jpeg' ? 'JPEG' : 'PNG'
      branding.logoDataUrl = await new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onloadend = () => resolve(reader.result)
        reader.onerror = reject
        reader.readAsDataURL(blob)
      })
    }
  } catch (e) {
    console.error('failed to load company branding', e)
  }
  return branding
}

/**
 * Fetches a consumer's loan rows and company branding, then generates the
 * Statement of Account PDF. Throws on data-fetch failure — the caller
 * handles the error popup.
 *
 * @param {object} args
 * @param {string} args.consumerId
 * @param {string} args.name
 * @param {string} args.address
 * @param {string} args.storeId
 */
export async function printStatementOfAccount({ consumerId, name, address, storeId }) {
  const { data: rows } = await consumerLoanRepository.statementOfAccount(
    consumerId,
    { storeId },
  )

  const branding = await getCompanyBranding()

  let totalCredit = 0
  let totalDue = 0
  const items = (rows || []).map((row) => {
    const invoice = Number(row.debt) || 0
    const paid = Number(row.paid) || 0
    const balance = invoice - paid
    totalCredit += invoice
    totalDue += balance
    return {
      dateDelivered: row.date_loaned ? row.date_loaned.dateFormat() : '',
      crNumber: row.id || '',
      dateDue: row.date_due ? row.date_due.dateFormat() : '',
      amountInvoice: formatAccounting(invoice),
      datePaid: row.date_paid ? row.date_paid.dateFormat() : '',
      amountPaid: formatAccounting(paid),
      balance: formatAccounting(balance),
      daysOverdue: computeDaysOverdue(row.date_due, balance),
      items: (row.items || []).map(item => ({
        item: item.product_name || item.stock_code || '',
        amount: formatAccounting(Number(item.amount) || 0),
      })),
    }
  })

  const statement = new StatementOfAccount()
  statement.generate(
    {
      companyName: branding.companyName,
      logoDataUrl: branding.logoDataUrl,
      logoFormat: branding.logoFormat,
      approvedByName: branding.approvedByName,
      approvedByPosition: branding.approvedByPosition,
      statementDate: getCurrentDateEnUsFormat(),
      name: name || '',
      address: address || '',
      totalAmountCredit: formatAccounting(totalCredit),
      totalDueAmount: formatAccounting(totalDue),
    },
    items,
  )
}
